import { t } from '../../i18next';
import { getAllDropdowns } from '../../shared/services/allDropdownsRegistry';
import { getDynamicLabel } from '../../shared/utils/labels';
import { getActiveValue, isActiveEqual } from './services/getActiveValue';

type ExtraValidatorResult = {
  isValid: boolean;
  errors: { [key: string]: { field: string; msg: string } };
};

const hasValue = (v: unknown) => {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim().length > 0;
  if (typeof v === 'number') return !Number.isNaN(v);
  return true;
};

const KEY_CATEGORY_MAP: Record<string, string> = {
  budget_cmm: 'CMM',
  psp_c_a_element_cmm: 'CMM',
  budget_ncp: 'NCP',
  psp_c_a_element_ncp: 'NCP',
  budget_femi: 'Hard Tooling',
  psp_c_a_element_femi: 'Hard Tooling',
};

// Helper: validate a single PSP element ↔ budget pair
const checkPairs = (
  row: Record<string, any>,
  elementKey: string,
  budgetKey: string,
  allColumnDefs: Array<any>,
  language: string,
  result: ExtraValidatorResult,
) => {
  const elementVal = row[elementKey];
  const budgetVal = row[budgetKey];
  const elementFilled = hasValue(elementVal);
  const budgetFilled = hasValue(budgetVal);
  const isIncomplete = (elementFilled && !budgetFilled) || (!elementFilled && budgetFilled);
  if (!isIncomplete) return;

  result.isValid = false;
  let missingKey: string;
  const isCmmBudgetMissing = !budgetFilled && budgetKey === 'budget_cmm';
  const hasBudgetCmmError = Boolean(result.errors['budget_cmm']);
  // Keep CMM pairing error on PSP element only when Rule 1 already set budget_cmm.
  if (isCmmBudgetMissing && hasBudgetCmmError) {
    missingKey = 'psp_c_a_element_cmm';
  } else if (!budgetFilled) {
    missingKey = budgetKey;
  } else {
    missingKey = elementKey;
  }
  const fieldLabel = getDynamicLabel(missingKey, allColumnDefs, language);

  const budget = KEY_CATEGORY_MAP[missingKey] ?? '';

  result.errors[missingKey] = {
    field: fieldLabel,
    msg: t('psp_management.pair_required', {
      budget,
    }),
  };
};

export async function extraValidator({
  row,
  allColumnDefs,
  language,
  originalRows,
}: {
  row: Record<string, any>;
  rows: Array<any>;
  tableDetails: any;
  allColumnDefs: Array<any>;
  language: string;
  originalRows: Array<any>;
}): Promise<ExtraValidatorResult> {
  const result: ExtraValidatorResult = { isValid: true, errors: {} };

  const allDropdowns = getAllDropdowns();
  const { yes: ACTIVE_YES } = getActiveValue(allDropdowns?.active || []);

  // Rule 1: At least one budget must be provided
  const budgetCmm = row['budget_cmm'];
  const budgetNcp = row['budget_ncp'];
  const budgetFemi = row['budget_femi'];
  const anyBudgetFilled = [budgetCmm, budgetNcp, budgetFemi].some(hasValue);
  if (!anyBudgetFilled) {
    result.isValid = false;
    const labelBudgetCmm = getDynamicLabel('budget_cmm', allColumnDefs, language);
    result.errors['budget_cmm'] = {
      field: labelBudgetCmm,
      msg: t('psp_management.budget_not_specified'),
    };
  }

  // Rule 2: Mandatory PSP element + budget pairing
  checkPairs(row, 'psp_c_a_element_cmm', 'budget_cmm', allColumnDefs, language, result);
  checkPairs(row, 'psp_c_a_element_ncp', 'budget_ncp', allColumnDefs, language, result);
  checkPairs(row, 'psp_c_a_element_femi', 'budget_femi', allColumnDefs, language, result);

  // Rule 4: Prevent multiple new records with the same PSP key at once
  // PSP key = design_owner_id + pf_lc + mp_nr
  // Check duplicates among rows being saved in this operation (status === 'N' and isCommitRow).
  if (Array.isArray(originalRows)) {
    const getKey = (r: any) =>
      `${r?.design_owner_id ?? ''}::${r?.pf_lc ?? ''}::${(r?.mp_nr ?? '').toString().trim()}`;

  const newCommitRows = originalRows.filter((r) => r?.status === 'N' || r?.status === 'C');

    const keyCounts = newCommitRows.reduce(
      (acc, r) => {
        const key = getKey(r);
        if (!key) return acc;
        acc[key] = (acc[key] ?? 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    const currentKey = getKey(row);
    // Only flag Rule 4 for new rows with status (N - new row, C - new Duplicate Row)
    if ((row?.status === 'N' || row?.status === 'C') && currentKey && keyCounts[currentKey] > 1) {
      result.isValid = false;
      const label = getDynamicLabel('mp_nr', allColumnDefs, language);
      result.errors['mp_nr'] = {
        field: label,
        msg: t('psp_management.psp_key_multi_create_not_allowed'),
      };
    }

    // Rule 5: Two PSPs cannot be active at once for the same PSP key among rows with status N or E
    // Skip Rule 5 if Rule 4 already applies for this row to avoid override.
    if (!((row?.status === 'N' || row?.status === 'C') && currentKey && keyCounts[currentKey] > 1)) {
      const candidateRows = originalRows.filter(
        (r) => r?.status === 'N' || r?.status === 'C' || r?.status === 'E',
      );

      const activeCountsByKey = candidateRows.reduce(
        (acc, r) => {
          const key = getKey(r);
          if (!key) return acc;
          const isYes = isActiveEqual(r?.active, ACTIVE_YES);
          if (isYes) acc[key] = (acc[key] ?? 0) + 1;
          return acc;
        },
        {} as Record<string, number>,
      );

      if (
        currentKey &&
        (activeCountsByKey[currentKey] ?? 0) > 1 &&
        isActiveEqual(row.active, ACTIVE_YES)
      ) {
        result.isValid = false;
        const label = getDynamicLabel('mp_nr', allColumnDefs, language);
        result.errors['mp_nr'] = {
          field: label,
          msg: t('psp_management.psp_key_active_not_unique'),
        };
      }
    }
  }

  return result;
}
